import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已下架</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white rounded-full px-6 py-2.5 text-sm transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ComicSeries',
          name: comic.title,
          description: comic.description,
          url: `http://rfeff.com/comic/${comic.slug}`,
          author: {
            '@type': 'Person',
            name: comic.author,
          },
          image: comic.coverImage,
          datePublished: comic.chapters[0]?.updatedAt,
          dateModified: comic.updatedAt,
          genre: comic.categories.map((cat) => categories.find((c) => c.slug === cat)?.name).filter(Boolean),
          keywords: comic.tags.join(', '),
          numberOfEpisodes: comic.chapters.length,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: comic.rating.toString(),
            bestRating: '5',
            reviewCount: comic.likes.toString(),
          },
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫之家',
            url: 'http://rfeff.com/',
          },
          potentialAction: {
            '@type': 'ReadAction',
            target: `http://rfeff.com/comic/${comic.slug}/read`,
            expectsAcceptanceOf: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'CNY',
              availability: 'https://schema.org/InStock',
            },
          },
        })}
      </script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '全部分类', to: '/category/all' },
              { label: comic.title },
            ]}
          />

          <ComicDetail comic={comic} />
          <ChapterList comic={comic} />
        </div>
      </main>

      <Footer />
    </>
  );
}

function ComicDetail({ comic }: { comic: Comic }) {
  return (
    <article className="py-8">
      <div className="flex flex-col md:flex-row gap-8 md:gap-10">
        <div className="w-full md:w-64 lg:w-72 shrink-0">
          <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
            <img
              src={comic.coverImage}
              alt={comic.title}
              title={`${comic.title} - 韩漫之家详情`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            {comic.title}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            作者：<strong>{comic.author}</strong>
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-accent-500 text-sm"></i>
              </div>
              <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
            </div>
            <span className="text-foreground-400 text-xs">|</span>
            <div className="flex items-center gap-1 text-foreground-500">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-sm"></i>
              </div>
              <span className="text-xs">{(comic.views / 10000).toFixed(0)}万 阅读</span>
            </div>
            <span className="text-foreground-400 text-xs">|</span>
            <div className="flex items-center gap-1 text-foreground-500">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-heart-line text-sm"></i>
              </div>
              <span className="text-xs">{(comic.likes / 1000).toFixed(0)}k 点赞</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
              comic.status === '连载中'
                ? 'bg-primary-100 text-primary-700'
                : comic.status === '已完结'
                ? 'bg-accent-100 text-accent-700'
                : 'bg-secondary-100 text-secondary-700'
            }`}>
              {comic.status}
            </span>
            {comic.tags.map((tag) => (
              <span key={tag} className="text-xs px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-600">
                {tag}
              </span>
            ))}
          </div>

          <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
            {comic.description}
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Link
              to={`/comic/${comic.slug}/read`}
              className="whitespace-nowrap inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white rounded-full px-8 py-3 text-sm font-medium transition-colors cursor-pointer"
            >
              开始阅读 <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/category/all"
              className="whitespace-nowrap inline-flex items-center bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full px-6 py-3 text-sm transition-colors cursor-pointer border border-background-200/70"
            >
              更多漫画
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function ChapterList({ comic }: { comic: Comic }) {
  const [showAll, setShowAll] = useState(false);
  const displayChapters = showAll ? comic.chapters : comic.chapters.slice(0, 10);

  return (
    <section className="py-8 border-t border-background-200/70">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-heading font-bold text-foreground-950">
          章节列表 <span className="text-sm font-normal text-foreground-500 font-sans">（共{comic.chapters.length}话）</span>
        </h2>
        <span className="text-xs text-foreground-500">
          最新更新: {comic.updatedAt}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {displayChapters.map((chapter) => (
          <Link
            key={chapter.id}
            to={`/comic/${comic.slug}/read?chapter=${chapter.number}`}
            className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-medium text-foreground-400 shrink-0">
                第{chapter.number}话
              </span>
              <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors truncate">
                {chapter.title}
              </span>
            </div>
            <span className="text-xs text-foreground-400 shrink-0 ml-2">
              {chapter.updatedAt}
            </span>
          </Link>
        ))}
      </div>

      {comic.chapters.length > 10 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-5 w-full py-2.5 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer text-center bg-background-100 rounded-lg hover:bg-background-200"
        >
          {showAll ? '收起' : `查看全部 ${comic.chapters.length} 话`}
        </button>
      )}
    </section>
  );
}